import { Check, Copy, Share, SquarePlus, type LucideIcon } from "lucide-react";
import { useId, type ReactNode } from "react";
import { Trans } from "react-i18next";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaCloseButton } from "@/shared/components/buttons/ColocaCloseButton";
import { ColocaIcon } from "@/shared/components/ColocaIcon";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { PwaInstallGuide } from "./pwaInstallGuide";

export interface PwaInstallCardProps {
  guide: PwaInstallGuide;
  linkCopied?: boolean;
  onInstall: () => void;
  onCopyLink: () => void;
  onDismiss: () => void;
  className?: string;
}

export function PwaInstallCard({
  guide,
  linkCopied = false,
  onInstall,
  onCopyLink,
  onDismiss,
  className,
}: PwaInstallCardProps) {
  const { t } = useTranslations("global");
  const titleId = useId();
  const emphasis = { b: <b className="font-semibold text-foreground" /> };

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "relative rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-lg shadow-black/10 dark:shadow-black/40",
        className,
      )}
    >
      <ColocaCloseButton
        aria-label={t("pwa_install_close")}
        size="sm"
        onClick={onDismiss}
        className="absolute top-2.5 right-2.5 text-muted-foreground shadow-none dark:bg-transparent"
      />

      <div className="flex items-start gap-3.5 pr-8">
        {/* The same artwork the home screen will show, so the person knows what to look for afterwards. */}
        <div
          aria-hidden
          className="flex size-13 shrink-0 items-center justify-center rounded-[22%] bg-[#00735C] shadow-sm ring-1 ring-black/5"
        >
          <ColocaIcon size="30" />
        </div>
        <div className="min-w-0 pt-0.5">
          <h2 id={titleId} className="text-base leading-tight font-semibold">
            {t("pwa_install_title")}
          </h2>
          <p className="mt-1 text-sm leading-snug text-muted-foreground">
            {guide === "ios-open-in-safari" ? t("pwa_install_body_open_in_safari") : t("pwa_install_body")}
          </p>
        </div>
      </div>

      {guide === "ios-safari" ? (
        <ol className="mt-4 flex flex-col gap-3 rounded-xl bg-muted/60 p-3">
          <InstallStep
            icon={Share}
            hint={t("pwa_install_ios_step_share_hint")}
            label={<Trans ns="global" i18nKey="pwa_install_ios_step_share" components={emphasis} />}
          />
          <InstallStep
            icon={SquarePlus}
            label={<Trans ns="global" i18nKey="pwa_install_ios_step_add" components={emphasis} />}
          />
        </ol>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <ColocaButton variant="ghost" onClick={onDismiss}>
          {t("pwa_install_later")}
        </ColocaButton>
        {guide === "prompt" ? (
          <ColocaButton variant="primary" onClick={onInstall} className="min-w-24">
            {t("pwa_install_action")}
          </ColocaButton>
        ) : null}
        {guide === "ios-open-in-safari" ? (
          <ColocaButton variant="primary" icon={linkCopied ? Check : Copy} onClick={onCopyLink}>
            {linkCopied ? t("pwa_install_link_copied_short") : t("pwa_install_copy_link")}
          </ColocaButton>
        ) : null}
      </div>
    </section>
  );
}

function InstallStep({ icon: Icon, label, hint }: { icon: LucideIcon; label: ReactNode; hint?: string }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden
        className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background shadow-xs"
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 pt-1 text-sm leading-snug">
        <p>{label}</p>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </li>
  );
}
